import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Button, TextField, Typography, Container, Paper, Alert,
    CircularProgress, createTheme, ThemeProvider, Stack, Snackbar,
    Accordion, AccordionSummary, AccordionDetails, Chip, Grid,
    Avatar, Badge, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, InputAdornment,
    LinearProgress, Divider, useMediaQuery, useTheme, Tooltip,
    CssBaseline, styled, alpha
} from '@mui/material';
import { Edit, ExpandMore, Save, PhotoCamera, Business, Add, Close, Delete, Group, CheckCircle, Cancel } from '@mui/icons-material';
import { IMaskInput } from 'react-imask';
import PropTypes from 'prop-types';
import api from '../services/api';
import useAuth from '../hooks/useAuth';

// --- TEMA IGUAL AO DA DASHBOARD ---
const customTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#315e9a', contrastText: '#ffffff' },
        secondary: { main: '#90caf9' },
        background: { default: '#0f1218', paper: '#1a1d24' },
        divider: 'rgba(0,0,0,0)', // <- evita erro do alpha('transparent')
    },
    typography: { button: { textTransform: 'none', fontWeight: 600 } },
    shape: { borderRadius: 12 },
});

// --- PAPER ESCURO PADRONIZADO ---
const DarkPaper = styled(Paper)(({ theme, selected }) => ({
    backgroundColor: selected ? alpha(theme.palette.primary.main, 0.15) : theme.palette.background.paper,
    backgroundImage: 'none',
    border: 'none',
    boxShadow: selected ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.5)}` : '0 8px 24px rgba(0,0,0,0.35)',
    transition: 'all 0.2s ease-in-out',
    borderRadius: 16,
}));

// --- ACCORDION ESCURO ---
const DarkAccordion = styled(Accordion)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    backgroundImage: 'none',
    border: 'none',
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    borderRadius: 16,
    overflow: 'hidden',
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: 0 },
    marginBottom: theme.spacing(2),
}));

// --- DIALOG DE CONFIRMAÇÃO (AJUSTADO) ---
const DeleteConfirmationDialog = ({ open, onClose, onConfirm, unitName, loading }) => {
    const [inputValue, setInputValue] = React.useState('');
    React.useEffect(() => { if (!open) setInputValue(''); }, [open]);
    const isConfirmationTextMatching = inputValue === unitName;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar Exclusão</DialogTitle>
            <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                <Alert severity="error" sx={{ mb: 2 }}>Esta ação é <strong>permanente e irreversível</strong>.</Alert>
                <Typography gutterBottom>Para confirmar, digite <strong>{unitName}</strong> no campo abaixo:</Typography>
                <TextField autoFocus fullWidth value={inputValue} onChange={(e) => setInputValue(e.target.value)} error={inputValue !== '' && !isConfirmationTextMatching} />
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={onConfirm} color="error" variant="contained" disabled={!isConfirmationTextMatching || loading}>
                    {loading ? <CircularProgress size={24} /> : "Excluir"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// --- MÁSCARAS ---
const CnpjMaskCustom = React.forwardRef((props, ref) => <IMaskInput {...props} mask="00.000.000/0000-00" inputRef={ref} onAccept={(value) => props.onChange({ target: { name: props.name, value } })} overwrite />);
const CepMaskCustom = React.forwardRef((props, ref) => <IMaskInput {...props} mask="00000-000" inputRef={ref} onAccept={(value) => props.onChange({ target: { name: props.name, value } })} overwrite />);
CnpjMaskCustom.propTypes = { name: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };
CepMaskCustom.propTypes = { name: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const unitLimits = { pequena: 2, media: 3, grande: 6 };

// --- VALIDAÇÃO DE CNPJ ---
function validateCnpj(cnpj) {
    const cleanedCnpj = cnpj.replace(/[^\d]/g, '');
    if (cleanedCnpj.length !== 14 || /^(\d)\1+$/.test(cleanedCnpj)) return false;
    let sum = 0, weights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    for (let i = 0; i < 12; i++) { sum += parseInt(cleanedCnpj.charAt(i)) * weights[i]; }
    let remainder = sum % 11;
    const firstDigit = remainder < 2 ? 0 : 11 - remainder;
    if (parseInt(cleanedCnpj.charAt(12)) !== firstDigit) return false;
    sum = 0; weights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    for (let i = 0; i < 13; i++) { sum += parseInt(cleanedCnpj.charAt(i)) * weights[i]; }
    remainder = sum % 11;
    const secondDigit = remainder < 2 ? 0 : 11 - remainder;
    if (parseInt(cleanedCnpj.charAt(13)) !== secondDigit) return false;
    return true;
}

// --- EDITOR DE UNIDADE (AJUSTADO) ---
const UnitEditor = ({ unit, onSave, onDelete, canEdit }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [unitData, setUnitData] = useState(unit);
    const [loading, setLoading] = useState(false);
    const [logoFile, setLogoFile] = useState(null);
    const fileInputRef = useRef(null);

    const createFullLogoUrl = (logoPath) => {
        if (!logoPath) return null;
        const baseUrl = process.env.REACT_APP_API_URL || '';
        try {
            if (baseUrl && baseUrl.startsWith('http')) return new URL(logoPath, baseUrl).href;
            return logoPath;
        } catch (error) {
            console.error("Erro ao criar URL da imagem:", error);
            return null;
        }
    };

    const [logoPreview, setLogoPreview] = useState(createFullLogoUrl(unit.logoUrl));

    const handleChange = (e) => setUnitData({ ...unitData, [e.target.name]: e.target.value });

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        const formData = new FormData();
        Object.keys(unitData).forEach(key => { if (unitData[key] !== null) formData.append(key, unitData[key]); });
        if (logoFile) formData.append('logo', logoFile);
        try {
            await onSave(unit.id, formData);
            setIsEditing(false);
        }
        catch (error) { console.error("Falha ao salvar unidade", error); }
        finally { setLoading(false); }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setUnitData(unit);
        setLogoFile(null);
        setLogoPreview(createFullLogoUrl(unit.logoUrl));
    };

    const userCount = unit._count?.users || 0;

    return (
        <DarkAccordion defaultExpanded={unit.isMatriz}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3, py: 0.5 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }} flexWrap="wrap">
                    <Avatar sx={{ bgcolor: unit.isMatriz ? 'primary.main' : 'grey.700', color: 'white' }}>
                        <Business />
                    </Avatar>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography fontWeight={600}>{unit.name}</Typography>
                        {unit.isMatriz && <Chip label="Matriz" color="primary" size="small" sx={{ fontWeight: 'bold' }} />}
                        <Chip
                            icon={<Group fontSize="small" />}
                            label={`${userCount} ${userCount === 1 ? 'usuário' : 'usuários'}`}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: 'rgba(255,255,255,0.1)' }}
                        />
                    </Stack>
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Box component="form" onSubmit={handleSave}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={canEdit && (
                                    <IconButton
                                        onClick={() => fileInputRef.current.click()}
                                        disabled={!isEditing}
                                        sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, width: 36, height: 36 }}
                                    >
                                        <PhotoCamera fontSize="small" />
                                    </IconButton>
                                )}
                            >
                                <Avatar key={logoPreview} sx={{ width: 100, height: 100, bgcolor: 'rgba(255,255,255,0.05)' }} src={logoPreview}>
                                    <Business sx={{ fontSize: 50 }} />
                                </Avatar>
                            </Badge>
                            <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={handleFileChange} />
                        </Grid>
                        <Grid item xs={12} md={9} container spacing={2}>
                            <Grid item xs={12}>
                                <TextField name="name" label="Nome da Unidade" value={unitData.name || ''} onChange={handleChange} fullWidth disabled={!isEditing} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField name="cnpj" label="CNPJ" value={unitData.cnpj || ''} onChange={handleChange} fullWidth disabled InputProps={{ readOnly: true }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField name="cep" label="CEP" value={unitData.cep || ''} onChange={handleChange} fullWidth disabled={!isEditing} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField name="address" label="Endereço" value={unitData.address || ''} onChange={handleChange} fullWidth disabled={!isEditing} />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <TextField name="city" label="Cidade" value={unitData.city || ''} onChange={handleChange} fullWidth disabled={!isEditing} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField name="state" label="Estado" value={unitData.state || ''} onChange={handleChange} fullWidth disabled={!isEditing} />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Stack
                        direction={{ xs: 'column-reverse', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems="center"
                        mt={3}
                        spacing={1}
                        sx={{ pt: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}
                    >
                        {(!unit.isMatriz && canEdit) ? (
                            <Button color="error" startIcon={<Delete />} onClick={() => onDelete(unit.id, unit.name)} disabled={isEditing}>
                                Excluir
                            </Button>
                        ) : <Box />}
                        <Stack direction="row" spacing={1} justifyContent="flex-end" flexGrow={1}>
                            {isEditing ? (
                                <>
                                    <Button onClick={handleCancelEdit}>Cancelar</Button>
                                    <Button type="submit" variant="contained" startIcon={<Save />} disabled={loading}>
                                        {loading ? <CircularProgress size={24} /> : "Salvar"}
                                    </Button>
                                </>
                            ) : (
                                canEdit && (
                                    <Button variant="contained" startIcon={<Edit />} onClick={() => setIsEditing(true)}>
                                        Editar Unidade
                                    </Button>
                                )
                            )}
                        </Stack>
                    </Stack>
                </Box>
            </AccordionDetails>
        </DarkAccordion>
    );
};

// --- FORM DE NOVA UNIDADE (AJUSTADO) ---
const NewUnitForm = ({ open, onClose, onSave }) => {
    const initialUnitState = { name: '', cnpj: '', cep: '', address: '', city: '', state: '', logo: null, logoPreview: null };
    const [unit, setUnit] = useState(initialUnitState);
    const [cnpjError, setCnpjError] = useState('');
    const [cnpjChecking, setCnpjChecking] = useState(false);
    const [cepLoading, setCepLoading] = useState(false);
    const [cepError, setCepError] = useState('');
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState('');

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === 'file') {
            if (files && files[0]) setUnit(p => ({ ...p, logo: files[0], logoPreview: URL.createObjectURL(files[0]) }));
        } else {
            setUnit(p => ({ ...p, [name]: value }));
        }
        if (name === 'cnpj') setCnpjError('');
    };

    const handleCnpjBlur = async (e) => {
        const cnpj = e.target.value;
        const cleanedCnpj = cnpj.replace(/[^\d]/g, '');
        if (cleanedCnpj.length !== 14) { setCnpjError(''); return; }
        if (!validateCnpj(cnpj)) { setCnpjError('Formato de CNPJ inválido.'); return; }
        setCnpjChecking(true); setCnpjError('');
        try {
            await api.get(`/organization/units/check-cnpj?cnpj=${cleanedCnpj}`);
        } catch (error) {
            if (error.response && error.response.status === 409) setCnpjError('Este CNPJ já está em uso.');
            else setCnpjError('Erro ao verificar CNPJ.');
        } finally { setCnpjChecking(false); }
    };

    const handleCepBlur = async (e) => {
        const cep = e.target.value.replace(/\D/g, '');
        if (cep.length !== 8) return;
        setCepLoading(true); setCepError('');
        try {
            const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await r.json();
            if (data.erro) throw new Error('CEP não encontrado.');
            setUnit(p => ({ ...p, cep: data.cep, address: data.logradouro, city: data.localidade, state: data.uf }));
        } catch (error) { setCepError(error.message); }
        finally { setCepLoading(false); }
    };

    const handleSaveClick = async () => {
        setLoading(true); setFormError('');
        if (!!cnpjError) {
            setFormError("O CNPJ informado é inválido ou já está em uso.");
            setLoading(false); return;
        }
        const formData = new FormData();
        formData.append('name', unit.name);
        formData.append('cnpj', unit.cnpj);
        formData.append('cep', unit.cep);
        formData.append('address', unit.address);
        formData.append('city', unit.city);
        formData.append('state', unit.state);
        if (unit.logo) formData.append('logo', unit.logo);
        try {
            await onSave(formData);
            handleClose();
        } catch (error) {
            setFormError(error.response?.data?.message || "Erro ao salvar a filial.");
        } finally { setLoading(false); }
    };

    const handleClose = () => { setUnit(initialUnitState); onClose(); };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle fontWeight="bold">Adicionar Nova Filial</DialogTitle>
            <DialogContent dividers sx={{ borderColor: 'transparent' }}>
                <Grid container spacing={2.5} sx={{ pt: 1 }}>
                    <Grid item xs={12}>
                        <TextField name="name" label="Nome da Filial" fullWidth required value={unit.name || ''} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            name="cnpj" label="CNPJ" fullWidth required
                            value={unit.cnpj || ''} onChange={handleChange} onBlur={handleCnpjBlur}
                            error={!!cnpjError} helperText={cnpjError}
                            InputProps={{
                                inputComponent: CnpjMaskCustom,
                                endAdornment: cnpjChecking && <InputAdornment position="end"><CircularProgress size={20} /></InputAdornment>
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            name="cep" label="CEP" fullWidth required
                            value={unit.cep || ''} onChange={handleChange} onBlur={handleCepBlur}
                            helperText={cepError || "Preencha para buscar o endereço"}
                            InputProps={{
                                inputComponent: CepMaskCustom,
                                endAdornment: cepLoading && <InputAdornment position="end"><CircularProgress size={20} /></InputAdornment>
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField name="address" label="Endereço" fullWidth required value={unit.address || ''} onChange={handleChange} InputLabelProps={{ shrink: !!unit.address }} />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextField name="city" label="Cidade" fullWidth required value={unit.city || ''} onChange={handleChange} InputLabelProps={{ shrink: !!unit.city }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField name="state" label="Estado" fullWidth required value={unit.state || ''} onChange={handleChange} InputLabelProps={{ shrink: !!unit.state }} />
                    </Grid>
                    <Grid item xs={12}>
                        <Button component="label" variant="outlined" fullWidth sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                            <input type="file" hidden onChange={handleChange} />
                            {unit.logoPreview ? 'Trocar Logo' : 'Enviar Logo'}
                        </Button>
                    </Grid>
                    {formError && <Grid item xs={12}><Alert severity="error">{formError}</Alert></Grid>}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button onClick={handleClose}>Cancelar</Button>
                <Button onClick={handleSaveClick} variant="contained" disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : 'Salvar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ============================================================================
// PÁGINA PRINCIPAL
// ============================================================================
const OrganizationManagementPage = () => {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'superadmin';
    const isAdmin = user?.role === 'admin';
    const canManage = isAdmin || isSuperAdmin;

    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [isNewUnitFormOpen, setIsNewUnitFormOpen] = useState(false);
    const [unitLicenseUsage, setUnitLicenseUsage] = useState({ used: 0, limit: 0 });
    const [userLicenseUsage, setUserLicenseUsage] = useState({ used: 0, limit: 0 });
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [unitToDelete, setUnitToDelete] = useState({ id: null, name: null });
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [isEditingName, setIsEditingName] = useState(false);
    const [organizationName, setOrganizationName] = useState('');
    const [savingName, setSavingName] = useState(false);

    const [allSoftwares, setAllSoftwares] = useState([]);
    const [mySoftwares, setMySoftwares] = useState([]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchOrganization = useCallback(async () => {
        if (!user?.companyId) { setLoading(false); return; }
        setLoading(true);
        try {
            const [orgRes, userUsageRes, allSoftwaresRes, mySoftwaresRes] = await Promise.all([
                api.get('/organization'),
                api.get('/users/usage'),
                api.get('/softwares'),
                api.get('/softwares/my-company')
            ]);
            setUserLicenseUsage(userUsageRes.data);
            setAllSoftwares(allSoftwaresRes.data);
            setMySoftwares(mySoftwaresRes.data);
            if (orgRes.data) {
                const sortedUnits = orgRes.data.units?.sort((a, b) => b.isMatriz - a.isMatriz) || [];
                const orgData = { ...orgRes.data, units: sortedUnits };
                setOrganization(orgData);
                setOrganizationName(orgData.name);
                const license = unitLimits[orgRes.data.licenseType?.toLowerCase()];
                if (license) setUnitLicenseUsage({ used: sortedUnits.length, limit: license });
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao carregar dados.');
        } finally { setLoading(false); }
    }, [user?.companyId]);

    useEffect(() => {
        if (user?.companyId) fetchOrganization();
    }, [user?.companyId, fetchOrganization]);

    const handleUpdateName = async () => {
        if (!organizationName.trim()) {
            setSnackbar({ open: true, message: 'O nome da empresa não pode ser vazio.', severity: 'warning' });
            return;
        }
        setSavingName(true);
        try {
            await api.put('/organization', { name: organizationName });
            setSnackbar({ open: true, message: 'Nome da empresa atualizado!', severity: 'success' });
            setIsEditingName(false);
            setOrganization(prev => ({ ...prev, name: organizationName }));
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'Falha ao atualizar o nome.', severity: 'error' });
        } finally { setSavingName(false); }
    };

    const handleCreateUnit = async (unitFormData) => {
        try {
            await api.post('/organization/units', unitFormData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setSnackbar({ open: true, message: 'Nova filial cadastrada!', severity: 'success' });
            fetchOrganization();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Falha ao cadastrar a nova filial.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
            throw new Error(errorMessage);
        }
    };

    const handleUpdateUnit = async (unitId, unitFormData) => {
        try {
            await api.put(`/organization/units/${unitId}`, unitFormData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setSnackbar({ open: true, message: 'Unidade atualizada!', severity: 'success' });
            fetchOrganization();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Falha ao atualizar a unidade.';
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
            throw new Error(errorMessage);
        }
    };

    const handleOpenDeleteModal = (unitId, unitName) => {
        setUnitToDelete({ id: unitId, name: unitName });
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!unitToDelete.id) return;
        setDeleteLoading(true);
        try {
            await api.delete(`/organization/units/${unitToDelete.id}`);
            setSnackbar({ open: true, message: 'Unidade excluída!', severity: 'success' });
            fetchOrganization();
            setIsDeleteModalOpen(false);
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.message || 'Falha ao excluir a unidade.', severity: 'error' });
        } finally {
            setDeleteLoading(false);
            setUnitToDelete({ id: null, name: null });
        }
    };

    if (loading) return (
        <ThemeProvider theme={customTheme}>
            <CssBaseline />
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
                <CircularProgress />
            </Box>
        </ThemeProvider>
    );

    if (error) return (
        <ThemeProvider theme={customTheme}>
            <CssBaseline />
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 4 }}>
                <Container maxWidth="lg"><Alert severity="error">{error}</Alert></Container>
            </Box>
        </ThemeProvider>
    );

    if (!organization) return (
        <ThemeProvider theme={customTheme}>
            <CssBaseline />
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 4 }}>
                <Container maxWidth="lg"><Alert severity="warning">Nenhuma organização encontrada.</Alert></Container>
            </Box>
        </ThemeProvider>
    );

    const unitProgress = unitLicenseUsage.limit > 0 ? (unitLicenseUsage.used / unitLicenseUsage.limit) * 100 : 0;
    const userProgress = userLicenseUsage.limit > 0 ? (userLicenseUsage.used / userLicenseUsage.limit) * 100 : 0;

    return (
        <ThemeProvider theme={customTheme}>
            <CssBaseline />
            <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default', pb: 8 }}>
                <Container maxWidth="lg" sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                        <Box>
                            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} color="text.primary">Minha Organização</Typography>
                            {isEditingName ? (
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                                    <TextField
                                        variant="standard"
                                        value={organizationName}
                                        onChange={(e) => setOrganizationName(e.target.value)}
                                        autoFocus
                                        onKeyDown={(e) => e.key === 'Enter' && handleUpdateName()}
                                        disabled={savingName}
                                        sx={{ '.MuiInput-input': { fontSize: '1.5rem', color: 'secondary.main' } }}
                                    />
                                    <IconButton color="primary" onClick={handleUpdateName} disabled={savingName}>
                                        {savingName ? <CircularProgress size={20} /> : <Save fontSize="small" />}
                                    </IconButton>
                                    <IconButton onClick={() => setIsEditingName(false)} disabled={savingName}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                </Stack>
                            ) : (
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Typography variant="h6" color="secondary.main">{organization.name}</Typography>
                                    {canManage && (
                                        <IconButton size="small" onClick={() => setIsEditingName(true)}>
                                            <Edit fontSize="inherit" sx={{ fontSize: '1rem' }} />
                                        </IconButton>
                                    )}
                                </Stack>
                            )}
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => setIsNewUnitFormOpen(true)}
                            disabled={!canManage || (!isSuperAdmin && unitLicenseUsage.used >= unitLicenseUsage.limit)}
                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                        >
                            Adicionar Filial
                        </Button>
                    </Stack>

                    {/* --- USO DE LICENÇAS --- */}
                    <DarkPaper sx={{ p: 3, mb: 4, background: alpha('#90caf9', 0.05) }}>
                        <Stack spacing={2}>
                            <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" fontWeight="bold" color="text.secondary">Uso da Licença de Unidades</Typography>
                                    <Typography variant="body2" fontWeight="bold">{unitLicenseUsage.used} / {unitLicenseUsage.limit}</Typography>
                                </Stack>
                                <LinearProgress variant="determinate" value={unitProgress} sx={{ height: 8, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.05)' }} />
                            </Stack>
                            <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                            <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" fontWeight="bold" color="text.secondary">Quantidade de Usuários</Typography>
                                    <Typography variant="body2" fontWeight="bold">{userLicenseUsage.used} / {userLicenseUsage.limit}</Typography>
                                </Stack>
                                <LinearProgress variant="determinate" value={userProgress} sx={{ height: 8, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.05)' }} />
                            </Stack>
                        </Stack>
                    </DarkPaper>

                    {/* --- PORTFÓLIO DE SOFTWARES --- */}
                    <DarkPaper sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Portfólio de Softwares</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Softwares disponíveis na plataforma. Os itens coloridos estão habilitados para sua empresa.
                        </Typography>
                        {allSoftwares.length === 0 ? (
                            <Typography color="text.secondary" fontStyle="italic">Nenhum software cadastrado no sistema.</Typography>
                        ) : (
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                {allSoftwares.map((software) => {
                                    const isEnabled = mySoftwares.some(s => s.id === software.id);
                                    return isEnabled ? (
                                        <Chip
                                            key={software.id}
                                            icon={<CheckCircle style={{ color: 'white' }} />}
                                            label={software.name}
                                            sx={{ fontWeight: 'bold', color: 'white', bgcolor: 'primary.main' }}
                                        />
                                    ) : (
                                        <Tooltip key={software.id} title="Não habilitado para sua empresa">
                                            <Chip
                                                icon={<Cancel />}
                                                label={software.name}
                                                variant="outlined"
                                                sx={{
                                                    borderColor: 'rgba(255,255,255,0.08)',
                                                    color: 'text.disabled',
                                                    opacity: 0.7,
                                                    '& .MuiChip-icon': { color: 'rgba(255,255,255,0.2)' }
                                                }}
                                            />
                                        </Tooltip>
                                    );
                                })}
                            </Stack>
                        )}
                    </DarkPaper>

                    {/* --- UNIDADES --- */}
                    <Box sx={{ p: { xs: 1, sm: 2 } }}>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2, px: { xs: 1, sm: 0 } }}>Unidades</Typography>
                        <Stack spacing={0}>
                            {organization.units.map(unit => (
                                <UnitEditor
                                    key={unit.id}
                                    unit={unit}
                                    onSave={handleUpdateUnit}
                                    onDelete={handleOpenDeleteModal}
                                    canEdit={canManage}
                                />
                            ))}
                        </Stack>
                    </Box>
                </Container>

                {isNewUnitFormOpen && (
                    <NewUnitForm open={isNewUnitFormOpen} onClose={() => setIsNewUnitFormOpen(false)} onSave={handleCreateUnit} />
                )}
                <DeleteConfirmationDialog
                    open={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={handleConfirmDelete}
                    unitName={unitToDelete?.name}
                    loading={deleteLoading}
                />
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </ThemeProvider>
    );
};

export default OrganizationManagementPage;